import { businessInfo } from "@/mocks/home";
import { useBooking } from "@/pages/home/components/BookingModal";

type ContactCard = {
  icon: string;
  title: string;
  lines: string[];
  actionLabel: string;
  actionHref?: string;
  external?: boolean;
  isBooking?: boolean;
};

export default function Contact() {
  const { openBooking } = useBooking();

  const contactCards: ContactCard[] = [
    {
      icon: "ri-map-pin-2-fill",
      title: "Visítanos",
      lines: [businessInfo.address],
      actionLabel: "Ver en Google Maps",
      actionHref: businessInfo.mapsLink,
      external: true,
    },
    {
      icon: "ri-time-fill",
      title: "Horario de atención",
      lines: [businessInfo.hoursFull, businessInfo.hoursNote],
      actionLabel: "Agenda tu visita",
      isBooking: true,
    },
    {
      icon: "ri-phone-fill",
      title: "Teléfono",
      lines: [businessInfo.phoneDisplay, "Llámanos para cotizar y agendar"],
      actionLabel: `Llamar al ${businessInfo.phoneDisplay}`,
      actionHref: businessInfo.phoneLink,
      external: false,
    },
  ];

  return (
    <section id="contacto" className="w-full bg-secondary-50">
      <div className="w-full px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold uppercase tracking-wider mb-5">
              <i className="ri-map-pin-line"></i>
              Contacto y ubicación
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight tracking-tight">
              Te esperamos en Tlalnepantla
            </h2>
            <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
              Estamos cerca de ti y abrimos temprano. Agenda tu cita por teléfono o pásanos a visitar: con gusto te
              atendemos.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-8 md:mb-10">
            {contactCards.map((card) => (
              <div
                key={card.title}
                className="bg-background-50 border border-background-200/80 rounded-lg p-6 md:p-7 flex flex-col"
              >
                <span className="w-14 h-14 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 mb-5">
                  <i className={`${card.icon} text-2xl`}></i>
                </span>
                <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-3">{card.title}</h3>
                <ul className="flex flex-col gap-2 mb-6">
                  {card.lines.map((line) => (
                    <li key={line} className="text-sm md:text-base text-foreground-600 leading-relaxed">
                      {line}
                    </li>
                  ))}
                </ul>
                {card.isBooking ? (
                  <button
                    type="button"
                    onClick={openBooking}
                    className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    {card.actionLabel}
                    <i className="ri-arrow-right-line"></i>
                  </button>
                ) : (
                  <a
                    href={card.actionHref}
                    target={card.external ? "_blank" : undefined}
                    rel={card.external ? "noreferrer" : undefined}
                    className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    {card.actionLabel}
                    <i className="ri-arrow-right-line"></i>
                  </a>
                )}
              </div>
            ))}
          </div>

          <div className="rounded-lg overflow-hidden border border-background-200/80 bg-background-50">
            <iframe
              title="Ubicación del Laboratorio Clínico ANEDAN en Tlalnepantla"
              src={businessInfo.mapsEmbed}
              className="w-full h-[320px] md:h-[420px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>

          <div className="mt-8 md:mt-10 bg-primary-600 rounded-lg p-7 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl font-semibold text-background-50">
                Agenda tu cita hoy mismo
              </h3>
              <p className="mt-2 text-base text-background-100/90">
                Una llamada es todo lo que necesitas. Te explicamos cómo prepararte y cuánto cuesta tu estudio.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <a
                href={businessInfo.phoneLink}
                className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-background-50 text-primary-700 text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill"></i>
                {businessInfo.phoneDisplay}
              </a>
              <a
                href={businessInfo.whatsappLink}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-background-50/10 border border-background-100/40 text-background-50 text-sm font-semibold hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-base"></i>
                Escríbenos
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}